import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useQuery, useQueries, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import MyContext from "../../context/MyContext";
import {
  getSubLocations,
  getTemplate,
  createBuildTemplate,
} from "../../api/services/scheduling";
import { QK } from "../../base/qk";
import CustomDate from "../CustomForm/CustomDate";

const BuildScheduleModal = ({ open, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedTemplates, setSelectedTemplates] = useState({});
  const queryClient = useQueryClient();
  const { locationID } = useContext(MyContext);
  const { enqueueSnackbar } = useSnackbar();

  const { data: subLocations } = useQuery(
    ["subLocations", locationID, selectedDate],
    () => getSubLocations(locationID, selectedDate),
    {
      enabled: !!locationID || !!selectedDate,
    }
  );

  const templatesQueries = useQueries(
    subLocations?.map((sublocation) => ({
      queryKey: ["templates", sublocation.id],
      queryFn: () => getTemplate(sublocation.id),
      enabled: !!sublocation.id,
    })) || []
  );

  const handleTemplateSelect = (sublocationId, templateId) => {
    setSelectedTemplates((prev) => ({
      ...prev,
      [sublocationId]: templateId,
    }));
  };

  const handleBuildTemplate = async (sublocationId) => {
    const templateId = selectedTemplates[sublocationId];
    try {
      const response = await createBuildTemplate({
        template: templateId,
        build_at: selectedDate,
        sublocation: sublocationId,
      });
      if (response.status === 201) {
        enqueueSnackbar("Build successful", { variant: "success" });
        queryClient.invalidateQueries(QK.subLocations);
        queryClient.invalidateQueries(QK.buildHistory);
      }
    } catch (error) {
      enqueueSnackbar(
        "Build Failed. Check your template if there is any visit available.",
        { variant: "error" }
      );
    } finally {
      setSelectedTemplates({});
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Build Schedule
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form">
          <CustomDate
            label="Select Date"
            onChange={(e) => setSelectedDate(e.target.value)}
            value={selectedDate}
          />

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ mb: 1 }}>
              Select Templates for Each Area
            </Typography>
            <List sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              {subLocations?.map((sublocation, index) => {
                const templates = templatesQueries[index]?.data || [];
                const isLoading = templatesQueries[index]?.isLoading;

                return (
                  <ListItem
                    key={sublocation.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Typography>{sublocation.name}</Typography>
                    {sublocation.is_build ? (
                      <Typography sx={{ color: "text.secondary" }}>
                        Already Built
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          value={selectedTemplates[sublocation.id] || ""}
                          onChange={(e) =>
                            handleTemplateSelect(
                              sublocation.id,
                              e.target.value
                            )
                          }
                          disabled={isLoading}
                          sx={{ minWidth: 180 }}
                        >
                          <MenuItem value="">Select Template</MenuItem>
                          {templates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.name}
                            </MenuItem>
                          ))}
                        </TextField>
                        {selectedTemplates[sublocation.id] && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                              handleBuildTemplate(sublocation.id)
                            }
                          >
                            Build
                          </Button>
                        )}
                      </Box>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BuildScheduleModal;